import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { S3BucketName } from 'src/app/shared/models/constants/s3-bucket-name';
import { S3DownloadURLResponseDTO } from 'src/app/shared/models/dtos/responses/s3-url-response-dto/s3-download-url-response-dto';
import { S3UploadURLResponseDTO } from 'src/app/shared/models/dtos/responses/s3-url-response-dto/s3-upload-url-response-dto';
import {
  MigrationUploadFile,
  TMigrationUploadFileDataType,
  MigrationUploadFileSupplyType,
} from 'src/app/shared/models/migration-upload-file/migration-upload-file';

@Injectable({
  providedIn: 'root',
})
/**
 * Data Service!
 */
export class DataService {
  private readonly backendAuthURL = '/dta/api/a';
  private readonly backendURL = '/dta/api';

  /**
   * Injects HttpClient to interact with LnR Backend
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * fetches s3 upload url from server
   * @param {BucketName} bucketName
   * @param {string} filename
   * @param {TMigrationUploadFileDataType} dataType
   * @param {TMigrationUploadFileSupplyType} supplyType
   * @param {number} regionId
   * @return {Observable<S3UploadURLResponseDTO>}
   */
  getS3UploadURL(
    bucketName: S3BucketName,
    filename: string,
    dataType: TMigrationUploadFileDataType,
    supplyType: MigrationUploadFileSupplyType,
    regionId: number
  ): Observable<S3UploadURLResponseDTO> {
    const endpoint = 'request/bucket-url';
    return this.httpClient.post<S3UploadURLResponseDTO>(
      this.buildURL(endpoint),
      {
        bucketName,
        originalFileName: filename,
        dataType,
        supplyType,
        regionId,
      }
    );
  }

  /**
   * @param {number} page
   * @param {number} pageSize
   * @param {number[]} regionIds
   * @param {string} dataType
   * @return {Observable<MigrationUploadFile[]>}
   */
  getMigrationUploadQueue(
    page: number,
    pageSize: number,
    regionIds: number[],
    dataType: string
  ): Observable<MigrationUploadFile[]> {
    const endpoint = 'request/status';
    return this.httpClient.get<MigrationUploadFile[]>(this.buildURL(endpoint), {
      params: {
        page,
        pageSize,
        dataType,
        regionIds,
      },
    });
  }

  /**
   * @param {number} requestId
   * @return {Observable<string>}
   */
  getS3DownloadURL(requestId: number): Observable<string> {
    const endpoint = `request/${requestId}/report`;
    return this.httpClient
      .get<S3DownloadURLResponseDTO>(this.buildURL(endpoint))
      .pipe(map((resp) => resp.downloadUrl));
  }

  /**
   * Gets a data service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }

  /**
   * Gets PORT points report download URL
   * @param {String} reportFileName
   * @return {Observable<string>}
   */
  getPortPointsReportDownloadURL(reportFileName: string): Observable<string> {
    const endpoint = 'port-points/report';

    return this.httpClient
      .get<S3DownloadURLResponseDTO>(this.buildURL(endpoint), {
        params: {
          filename: reportFileName,
        },
      })
      .pipe(map((resp) => resp.downloadUrl));
  }

  /**
   * Builds URL from endpoint with authentication
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }
}
